import React, { useContext } from 'react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { flowRight } from 'lodash';
import cx from 'classnames';
import PageHeader from '../../components/page-header/PageHeader.component';
import {
  getProduct,
  getProductConfig
} from '../../utilities/product.utilities';
import { useHistory } from 'react-router';
import { RoutePath } from '../../components/app/app.constants';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { RecommendationContentTypeDto } from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import RecommendationPostPlayerNext from '../../components/remediation-post-player-next/RemediationPostPlayerNext.component';
import RemediationQuiz from '../../components/remediation-quiz/RemediationQuiz.component';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  getAssessmentGoalFromRecommendation,
  getNextRecommendation,
  getQuizTotals,
  getTotalProgress
} from '../../utilities/remediation.utilities';
import { trackAdobeAction } from '../../components/app/app-state-actions.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { SherpathAIChatSidebar } from '../../components/sherpath-ai-chat-sidebar/SherpathAIChatSidebar.component';
import { SherpathAIChatToggle } from '../../components/sherpath-ai-chat-toggle/SherpathAIChatToggle.component';
import { BackToPlanButton } from '../../components/back-to-plan-button/BackToPlanButton.component';
import {
  buildSherpathAIPageContext,
  PageContextType
} from '../../utilities/sherpath-ai-context.utilities';
import { getIsAIChatPageContextEnabled } from '../../components/app/app-state-selectors.utilities';

export type NextRecommendationProps = ELSPropsFromModalService;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const NextRecommendationComponent = (): JSX.Element => {

  const {
    appState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const { header } = getProductConfig(getProduct(baseState?.remediationAssignmentWithSources));
  const history = useHistory();

  const nextRecommendation = getNextRecommendation(baseState, null);

  const getPageContext = (): string => {
    if (!getIsAIChatPageContextEnabled(appState)) {
      return null;
    }
    return buildSherpathAIPageContext({
      remediationAssignmentWithSources: baseState?.remediationAssignmentWithSources,
      assessmentMetrics: baseState?.assessmentMetrics,
      assessmentSources: baseState?.raasAssessmentSourceList,
      hesiExamInfo: baseState?.hesiExamInfo,
      totalProgressPercent: getTotalProgress(baseState),
      pageType: PageContextType.POST_PLAYER,
      pageSpecificData: null
    });
  };

  const handleReturnNavigation = () => {
    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:closeNextRecommendation`,
        }
      }
    });
    history.push(RoutePath.REMEDIATION_STUDENT_HOME);
  };

  if (!nextRecommendation) {
    return null;
  }

  const assessmentGoal = getAssessmentGoalFromRecommendation(baseState?.eolsAssessment, nextRecommendation);
  const quizStatusCounts = getQuizTotals(baseState);

  return (
    <div className="c-ssa-remediation-post-player">
      <div className="c-ssa-remediation-post-player__header">
        <PageHeader onCloseClick={handleReturnNavigation} title={header.title} titleClass={header.titleClass} logo={header.logo}>
          <SherpathAIChatToggle
            remediationAssignmentWithSources={baseState?.remediationAssignmentWithSources}
            pageContextType={PageContextType.POST_PLAYER}
          />
        </PageHeader>
      </div>

      <div className="c-ssa-remediation-post-player__main">
        <div className={cx('c-ssa-remediation-post-player__body', {
          'c-ssa-remediation-post-player__body--chat-active': appState.isChatActive
        })}>

          <div className="c-ssa-remediation-post-player__window">


            {
              nextRecommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ && (
                <div className="o-els-container">
                  <RemediationQuiz
                    assessmentGoal={assessmentGoal}
                    recommendation={nextRecommendation}
                    metricCounts={quizStatusCounts}
                    showReviewQuizButton={false}
                    continueQuizzing
                    showBorder
                  />
                </div>
              )
            }

            {
              nextRecommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ && (
                <div>
                  <h3 className="u-els-text-center u-els-padding-bottom-1x"><strong>Next recommendation</strong></h3>
                  <div className="o-els-container">
                    <RecommendationPostPlayerNext
                      nextRecommendation={nextRecommendation}
                    />
                  </div>
                </div>
              )
            }

            <div className="u-els-margin-top-2x">
              <FlexLayout modifiers={[
                FlexLayoutModifier.GUTTERS,
                FlexLayoutModifier.CENTER,
              ]}>

                <FlexItem>
                  <BackToPlanButton
                    appStateRef={appStateRef}
                    recommendationId={nextRecommendation?.id}
                    contentType={nextRecommendation?.contentType}
                  />
                </FlexItem>

              </FlexLayout>
            </div>

          </div>

        </div>

        <SherpathAIChatSidebar
          remediationAssignmentWithSources={baseState?.remediationAssignmentWithSources}
          pageContext={getPageContext()}
          pageContextType={PageContextType.POST_PLAYER}
        />
      </div>

    </div>
  );
};

const enhance = flowRight(ELSWithModalService);

const NextRecommendation: React.FC<Record<string, never>> = enhance(NextRecommendationComponent);

export default NextRecommendation;
